import { useEffect } from "react";
import type { ParsedLocation } from "@tanstack/react-router";

// Rails sets this header on every response to the fingerprint of the frontend it serves.
export const BUNDLE_VERSION_HEADER = "X-Bundle-Version";
export const BUNDLE_CHECK_PATH = "/up";
export const BUNDLE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
// A navigation waits at most this long for a check, so a slow network never stalls the app.
export const BUNDLE_CHECK_TIMEOUT_MS = 1000;

type StaleBundleGuardOptions = {
  bundleVersion?: string | null;
  checkTimeoutMs?: number;
  // False while the tab holds work a reload would lose, such as an upload in progress.
  canReload?: () => boolean;
};

const reloadHolds = new Set<symbol>();

// Defers the stale-bundle reload while `hold` is true, for work or unsaved input a reload would lose.
export function useHoldStaleBundleReload(hold: boolean) {
  useEffect(() => {
    if (!hold) return;
    const key = Symbol("reload hold");
    reloadHolds.add(key);
    return () => {
      reloadHolds.delete(key);
    };
  }, [hold]);
}

export const isStaleBundleReloadHeld = () => reloadHolds.size > 0;

export type StaleBundleGuard = {
  beforeLoad: (args: { location: ParsedLocation; preload: boolean }) => Promise<void>;
  uninstall: () => void;
};

// Turns a path navigation into a full page load once the server serves a newer frontend. Refocusing
// the tab or changing search params never reloads, so half-filled forms and typed filters survive.
export function createStaleBundleGuard({
  bundleVersion = window.bundleVersion,
  checkTimeoutMs = BUNDLE_CHECK_TIMEOUT_MS,
  canReload = () => true,
}: StaleBundleGuardOptions = {}): StaleBundleGuard {
  if (!bundleVersion) {
    return { beforeLoad: async () => {}, uninstall: () => {} };
  }

  let stale = false;
  let check: Promise<void> | undefined;
  let lastCheckedAt = Date.now();
  let lastPathname: string | undefined;

  // Returns the in-flight check, so a navigation during a focus-triggered check waits for it too.
  const checkServerBundle = (): Promise<void> | undefined => {
    if (check || stale || Date.now() - lastCheckedAt < BUNDLE_CHECK_INTERVAL_MS) {
      return check;
    }
    lastCheckedAt = Date.now();
    check = (async () => {
      try {
        const response = await fetch(new URL(BUNDLE_CHECK_PATH, window.location.origin), {
          method: "HEAD",
          cache: "no-store",
        });
        const serverVersion = response.headers.get(BUNDLE_VERSION_HEADER);
        stale = !!serverVersion && serverVersion !== bundleVersion;
      } catch (_) {
        // Offline or mid-deploy: the next visibility change or navigation retries.
      } finally {
        check = undefined;
      }
    })();
    return check;
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void checkServerBundle();
    }
  };
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    beforeLoad: async ({ location, preload }) => {
      if (preload) return;
      const pathChanged = lastPathname !== undefined && location.pathname !== lastPathname;
      lastPathname = location.pathname;
      if (!pathChanged) return;

      const inFlight = checkServerBundle();
      if (inFlight) {
        await Promise.race([
          inFlight,
          new Promise((resolve) => setTimeout(resolve, checkTimeoutMs)),
        ]);
      }
      if (!stale || !canReload()) return;

      // History already points at the destination, so the reload lands where the user clicked.
      window.location.reload();
      // Holds the current page until the browser unloads, so the old code never renders the next one.
      await new Promise(() => {});
    },
    uninstall: () => document.removeEventListener("visibilitychange", onVisibilityChange),
  };
}
